import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Plus, Trash, Percent, CurrencyDollar } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
export const PromotionsManager = ({ outletId }) => {
  const [promos, setPromos] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', type: 'percent', value: 10, min_order: 0 });

  const fetchPromos = useCallback(async () => {
    if (!outletId) return;
    try {
      const r = await axios.get(`${API_URL}/api/promotions?outlet_id=${outletId}`, { withCredentials: true });
      setPromos(r.data);
    } catch (e) { console.error(e); }
  }, [outletId]);

  useEffect(() => { fetchPromos(); }, [fetchPromos]);

  const add = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error('Code and name required'); return; }
    try {
      await axios.post(`${API_URL}/api/promotions`, {
        ...form, outlet_id: outletId, value: parseFloat(form.value), min_order: parseFloat(form.min_order),
      }, { withCredentials: true });
      toast.success('Promotion created');
      setForm({ code: '', name: '', type: 'percent', value: 10, min_order: 0 });
      setShowAdd(false);
      fetchPromos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await axios.delete(`${API_URL}/api/promotions/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchPromos();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <Card className="border border-[var(--theme-border)] bg-[var(--theme-surface)]" data-testid="promotions-manager">
      <CardHeader className="border-b border-[var(--theme-border)] flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2 text-[var(--theme-text)]">
          <Gift size={20} className="text-[var(--theme-accent)]" />
          Promotions & Coupons
        </CardTitle>
        <Button size="sm" onClick={() => setShowAdd((v) => !v)} className="bg-[var(--theme-accent)] text-white" data-testid="add-promo-toggle">
          <Plus size={14} className="mr-1" /> New
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {showAdd && (
          <div className="rounded-xl border border-dashed border-[var(--theme-border)] p-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end" data-testid="add-promo-form">
            <div>
              <Label className="text-xs">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SAVE10" data-testid="promo-code-input" />
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="10% Off" data-testid="promo-name-input" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent %</SelectItem>
                  <SelectItem value="fixed">Fixed $</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Value</Label>
              <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="promo-value-input" />
            </div>
            <Button onClick={add} className="bg-[var(--theme-accent)] text-white" data-testid="promo-save-btn">Save</Button>
          </div>
        )}

        <div className="space-y-2" data-testid="promo-list">
          {promos.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-3 rounded-xl border border-[var(--theme-border)]" data-testid={`promo-${p.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[var(--theme-accent)]/10 flex items-center justify-center text-[var(--theme-accent)]">
                  {p.type === 'percent' ? <Percent size={18} /> : <CurrencyDollar size={18} />}
                </div>
                <div>
                  <p className="font-mono font-bold text-[var(--theme-text)]">{p.code}</p>
                  <p className="text-xs text-[var(--theme-text-muted)]">
                    {p.name} · {p.type === 'percent' ? `${p.value}% off` : `$${p.value} off`}
                    {p.min_order > 0 ? ` · min $${p.min_order}` : ''}
                    {p.used_count > 0 ? ` · used ${p.used_count}×` : ''}
                  </p>
                </div>
              </div>
              <button onClick={() => del(p.id)} className="text-[var(--theme-text-muted)] hover:text-red-500 p-1" data-testid={`promo-delete-${p.id}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
          {promos.length === 0 && (
            <p className="text-sm text-[var(--theme-text-muted)] text-center py-4">
              No promotions yet. Create your first coupon to reward customers.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PromotionsManager;
